import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import { accountApi } from '../api/client';
import { useLanguage } from '../lib/i18n';
import { formatCurrency } from '../lib/currency';

const STATUS_LABELS = {
  PENDING_PAYMENT: 'Wacht op betaling', confirmed: 'Bevestigd', processing: 'In verwerking',
  shipped: 'Verzonden', delivered: 'Geleverd', cancelled: 'Geannuleerd', refunded: 'Terugbetaald',
};
const STATUS_COLORS = {
  PENDING_PAYMENT: 'bg-yellow-100 text-yellow-800', confirmed: 'bg-blue-100 text-blue-800',
  processing: 'bg-blue-100 text-blue-800', shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800',
};

export default function MyOrderDetail() {
  const { orderNumber } = useParams();
  const { locale } = useLanguage();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    accountApi.getOrder(orderNumber).then(r => setOrder(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [orderNumber]);

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-12"><div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin mx-auto" /></div>;
  if (!order) return (
    <div className="max-w-3xl mx-auto px-6 py-12 text-center">
      <p className="font-serif text-xl text-muted-foreground mb-4">Bestelling niet gevonden</p>
      <Link to="/account" className="text-sm text-primary font-medium hover:underline">Terug naar account</Link>
    </div>
  );

  const addr = order.deliveryAddressSnapshot || {};

  return (
    <div data-testid="my-order-detail" className="max-w-3xl mx-auto px-6 md:px-12 py-12">
      <Link to="/account" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6 font-medium">
        <ArrowLeft className="w-4 h-4" /> Mijn account
      </Link>
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-light text-foreground">Bestelling #{order.orderNumber}</h1>
          <p className="text-sm text-muted-foreground mt-1">{order.created_at ? new Date(order.created_at).toLocaleDateString('nl-BE', { dateStyle: 'long' }) : ''}</p>
        </div>
        <span className={`px-4 py-1.5 rounded-full text-sm font-semibold ${STATUS_COLORS[order.status] || 'bg-muted text-muted-foreground'}`}>
          {STATUS_LABELS[order.status] || order.status}
        </span>
      </div>

      {/* Lines */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden mb-6 shadow-sm">
        <div className="px-6 py-4 border-b border-border bg-muted/50">
          <h2 className="font-serif text-lg font-light text-foreground">Artikelen</h2>
        </div>
        <div className="divide-y divide-border">
          {(order.lines || []).map(line => {
            const name = line[`nameSnapshot_${locale}`] || line.nameSnapshot_nl;
            const price = line.effectiveUnitPrice || line.priceSnapshot;
            return (
              <div key={line._id} className="flex items-center justify-between px-6 py-4 text-sm">
                <div>
                  <span className="text-foreground">{name} <span className="text-muted-foreground">x {line.quantity}</span></span>
                  {line.freeUnits > 0 && <span className="ml-2 text-xs text-green-700">({line.freeUnits} gratis)</span>}
                </div>
                <span className="font-semibold">{formatCurrency(price * line.quantity, locale)}</span>
              </div>
            );
          })}
        </div>
        {order.shippingCost > 0 && (
          <div className="flex items-center justify-between px-6 py-3 text-sm border-t border-border">
            <span className="text-muted-foreground">Verzendkosten</span>
            <span className="font-semibold">{formatCurrency(order.shippingCost, locale)}</span>
          </div>
        )}
        <div className="px-6 py-4 border-t-2 border-primary flex items-center justify-between">
          <span className="font-serif text-base text-foreground">Totaal</span>
          <span className="font-semibold text-lg">{formatCurrency(order.totalAmount, locale)}</span>
        </div>
      </div>

      {/* Delivery + VAT */}
      <div className="bg-card border border-border rounded-2xl p-6 mb-6 shadow-sm space-y-3 text-sm">
        {addr.street && (
          <div className="flex gap-3"><span className="text-muted-foreground w-32 shrink-0 font-medium">Leveringsadres</span>
            <span className="text-foreground">{addr.street} {addr.number}{addr.bus ? ` bus ${addr.bus}` : ''}, {addr.postalCode} {addr.city}</span>
          </div>
        )}
        {order.vatReversed && (
          <div className="rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-xs px-3 py-2 mt-2">
            <p className="font-semibold">BTW verlegd — intracommunautaire levering, Art. 39bis W.BTW.</p>
          </div>
        )}
      </div>

      {/* Invoice download */}
      <a
        href={accountApi.getInvoiceUrl(order.orderNumber)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="download-invoice"
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-primary text-primary text-sm font-semibold hover:bg-primary hover:text-primary-foreground transition-all"
      >
        <Download className="w-4 h-4" /> Download factuur
      </a>
    </div>
  );
}
